import type { ReactNode } from 'react';
import { Tag, UserPlus } from 'lucide-react';
import type { TaskBoard, TaskColumn, TaskLabel, TaskLabelColor } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createLabelNow } from './label-actions';
import { AssigneePicker, LabelPicker } from './TaskMemberLabelPickers';
import { AvatarStack } from './task-ui';

export interface TaskFieldValues {
  columnId: string;
  /** A type's id, or `typeLead.value` for the choice before the types (Suggest, Default type). */
  typeId: string;
  assigneeIds: string[];
  labelIds: string[];
}

/**
 * A new task's column, type, people and labels, as one row of small controls: the New task dialog,
 * and a report schedule's task (T-337). People who left and labels deleted since the values were
 * saved are dropped, so nothing stays selected out of sight. `children` go on the end of the row
 * (the New task dialog's Link).
 */
export function TaskFieldControls({
  meta,
  columns = meta.columns,
  value,
  onChange,
  typeLead,
  onCreateLabel = (name, color) => createLabelNow(meta.labels, name, color),
  children,
}: {
  meta: Pick<TaskBoard, 'members' | 'labels' | 'columns' | 'types'>;
  /** The columns to offer, when not all of them (a schedule's task can't start in a done column). */
  columns?: TaskColumn[];
  value: TaskFieldValues;
  onChange: (next: TaskFieldValues) => void;
  /** The type choice listed before the types: what happens when none is picked. */
  typeLead: { value: string; label: ReactNode };
  /** Makes a label typed in the picker; by default saved to the board straight away. */
  onCreateLabel?: (name: string, color: TaskLabelColor) => TaskLabel | null | Promise<TaskLabel | null>;
  children?: ReactNode;
}) {
  const assigneeIds = value.assigneeIds.filter((id) => meta.members.some((m) => m.user_id === id));
  const labelIds = value.labelIds.filter((id) => meta.labels.some((l) => l.id === id));
  const columnId = columns.some((c) => c.id === value.columnId) ? value.columnId : (columns[0]?.id ?? '');
  const typeId = meta.types.some((t) => t.id === value.typeId) ? value.typeId : typeLead.value;
  // Hands back what's shown, so an untouched column or type is the one on screen, never ''.
  const set = (patch: Partial<TaskFieldValues>) =>
    onChange({ columnId, typeId, assigneeIds, labelIds, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={columnId} onValueChange={(id) => set({ columnId: id })}>
        <SelectTrigger size="sm" className="w-40" aria-label="Column">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {columns.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {meta.types.length ? (
        <Select value={typeId} onValueChange={(id) => set({ typeId: id })}>
          <SelectTrigger size="sm" className="w-40" aria-label="Type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={typeLead.value}>{typeLead.label}</SelectItem>
            {meta.types.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      <AssigneePicker members={meta.members} selected={assigneeIds} onChange={(ids) => set({ assigneeIds: ids })}>
        <Button type="button" variant="outline" size="sm">
          {assigneeIds.length ? (
            <AvatarStack userIds={assigneeIds} members={meta.members} />
          ) : (
            <UserPlus className="size-4" />
          )}
          Assign
        </Button>
      </AssigneePicker>
      <LabelPicker
        labels={meta.labels}
        selected={labelIds}
        onChange={(ids) => set({ labelIds: ids })}
        onCreate={onCreateLabel}
      >
        <Button type="button" variant="outline" size="sm">
          <Tag className="size-4" />
          Labels
        </Button>
      </LabelPicker>
      {children}
    </div>
  );
}
